import { Link, usePage } from "@inertiajs/react"
import type { ReactNode } from "react"

import type { SharedData } from "./shared"

interface AdminLayoutProps {
  children: ReactNode
  // Every admin screen but the root uses gap-4 (docs/design/admin.html);
  // the root's own two <section>s (trip card, day list) need gap-6 — the
  // one class the <main> below varies per screen. t010 `impl-admin-tabs`
  // corrects this comment's own former claim ("every other class below
  // stays byte-identical to the design contract on every page"): the
  // header below no longer matches docs/design/admin.html at all — it now
  // follows docs/design/contacts.html's amended Block 2 (the tab row,
  // FR-5.1), the same restructure landing on every admin screen at once.
  mainClassName?: string
}

const DEFAULT_MAIN_CLASSES = "flex flex-col gap-4 p-4"

// t010 `impl-admin-tabs` (R-2a): the contract's own text-gray-500 for the
// inactive admin tab. Unlike the public Layout's own tabClass (Layout.tsx,
// FR-5.3 overrides the contract there to keep the landed text-gray-400),
// nothing had landed on this header before this child, so the contract
// governs outright. t016 `impl-admin-chrome` (FR-20, docs/design/
// stays.html Block 2) adds `whitespace-nowrap` to every tab, so a fourth
// tab shrinks the row's available space rather than wrapping any label.
function tabClass(active: boolean): string {
  return active
    ? "whitespace-nowrap border-b-2 border-accent pb-2 font-medium text-accent"
    : "whitespace-nowrap pb-2 text-gray-500"
}

// t011 `impl-chrome` (FR-9.2): «На сайт →»'s target, derived from the
// current URL rather than hard-wired to `/` — the public `Layout`'s own
// `manageHref` mirrored for the opposite direction. A day address and its
// item forms under it (new/create only — the shallow edit/update/destroy
// item routes carry no date, plan R-1) resolve to that day's own public
// page; a contacts address and its forms resolve to the public contact
// list; `/admin/users`, `/admin/trip` and anything else (including the
// shallow item forms) fall through to the public root. t016
// `impl-admin-chrome` (FR-27) deliberately does not add an
// `/admin/stays` branch: there is no public stays screen, so it falls
// through to the existing default `/` branch like `/admin/users` does.
function siteHref(url: string): string {
  const dayMatch = url.match(/^\/admin\/days\/(\d{4}-\d{2}-\d{2})/)
  if (dayMatch) return `/days/${dayMatch[1]}`
  if (url.startsWith("/admin/contacts")) return "/contacts"
  // t020 `impl-chrome` (FR-5, AC-19): the public Layout's own `manageHref`
  // mirrored for the opposite direction, the `/admin/contacts` branch
  // above's own precedent -- `flights` does have a public page, unlike
  // `stays`, so this joins `/admin/contacts` rather than falling through
  // to the bare `/` default.
  if (url.startsWith("/admin/flights")) return "/flights"
  return "/"
}

// The admin area's own chrome (FR-1): a header on a 15%-accent wash
// (`bg-accent/15`, distinct from the public Layout's white header)
// carrying the area's name, a link back to the public root, and — since
// t010 `impl-admin-tabs` (FR-5.1, docs/design/contacts.html's amended
// Block 2) — the «Расписание | Контакты» tab row every admin screen now
// shares. Every admin screen wraps its content in this component instead
// of the public `Layout` — the admin area never renders inside it
// (docs/design/admin.html's own header comment). Sanctioned in
// `app/shared` on the same precedent as the public `Layout.tsx`
// (domain-structure's app-shell exception): both are cross-domain
// navigation chrome, not content.
//
// `admin`'s two strings arrive as Inertia shared data
// (`Identity::AdminOnly`'s own `inertia_share`), not props — every admin
// controller that includes that gate gets them for free, with nothing to
// pass explicitly. `ui` (the tab labels, FR-5.2) and `url` (which tab is
// active) need no new sharing of their own: `ui` already reaches every
// request, admin included (`app/shared/application_controller.rb`'s own
// `inertia_share`, the same source the public `Layout` reads), and `url`
// rides `usePage()`'s own page object rather than `props` at all.
export default function AdminLayout({ children, mainClassName = DEFAULT_MAIN_CLASSES }: AdminLayoutProps) {
  // t009 (revision): `SharedData.admin` is typed optional because the
  // server only populates it on the seven `Identity::AdminOnly`-gated
  // controllers (app/shared/shared.ts) — this non-null assertion is where
  // that invariant is documented, since AdminLayout itself only ever
  // renders on those already-gated pages. `ui` carries no such guard: it
  // is unguarded shared data, present on every response.
  const { props, url } = usePage<SharedData>()
  const { admin, ui } = props
  const { heading, backToSite } = admin!
  // t010 `impl-admin-tabs` (FR-5.1), extended by t011 `impl-chrome`
  // (FR-7.1, FR-7.2) to a three-way rule, by t016 `impl-admin-chrome`
  // (FR-20) to a four-way one, and by t020 `impl-chrome` (FR-5.2, AC-19)
  // to a five-way one: contacts is active for `/admin/contacts` and every
  // address under it, flights for `/admin/flights` and everything under
  // it, stays for `/admin/stays` and everything under it, users for
  // `/admin/users` and everything under it, schedule everywhere else —
  // the same "derive from the current URL" idiom the public Layout's own
  // contactsActive uses, mirrored for the admin route prefixes.
  const contactsActive = url.startsWith("/admin/contacts")
  const flightsActive = url.startsWith("/admin/flights")
  const staysActive = url.startsWith("/admin/stays")
  const usersActive = url.startsWith("/admin/users")
  const scheduleActive = !contactsActive && !flightsActive && !staysActive && !usersActive

  return (
    <div className="mx-auto min-h-screen max-w-xl">
      <header className="border-b border-gray-200 bg-accent/15 px-4 pt-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold text-gray-900">{heading}</h1>
          <Link href={siteHref(url)} className="text-[13px] leading-[18px] text-accent">
            {backToSite}
          </Link>
        </div>
        {/* t020 `impl-chrome` (FR-5.3): a fifth tab exceeds a 375px
            viewport, so the row scrolls horizontally instead of wrapping
            -- `overflow-x-auto` joins the row's existing `flex
            flex-nowrap`; every tab already carries `whitespace-nowrap`
            (t016), so no label itself ever wraps either. */}
        <nav className="mt-3 flex flex-nowrap gap-6 overflow-x-auto text-sm">
          <Link
            href="/admin"
            className={tabClass(scheduleActive)}
            aria-current={scheduleActive ? "page" : undefined}
          >
            {ui.scheduleTab}
          </Link>
          <Link
            href="/admin/contacts"
            className={tabClass(contactsActive)}
            aria-current={contactsActive ? "page" : undefined}
          >
            {ui.contactsTab}
          </Link>
          {/* t020 `impl-chrome` (FR-5.2, R-1): the fifth tab, positioned
              third in site order per design Block 2 -- «Расписание |
              Контакты | Рейсы | Жильё | Пользователи». */}
          <Link
            href="/admin/flights"
            className={tabClass(flightsActive)}
            aria-current={flightsActive ? "page" : undefined}
          >
            {ui.flightsTab}
          </Link>
          <Link
            href="/admin/stays"
            className={tabClass(staysActive)}
            aria-current={staysActive ? "page" : undefined}
          >
            {ui.staysTab}
          </Link>
          <Link
            href="/admin/users"
            className={tabClass(usersActive)}
            aria-current={usersActive ? "page" : undefined}
          >
            {ui.usersTab}
          </Link>
        </nav>
      </header>
      <main className={mainClassName}>{children}</main>
    </div>
  )
}
